import React, { useEffect, useMemo, useRef } from 'react';
import { Application, Sprite } from 'pixi.js';
import { TimelineState } from '@xzdarcy/react-timeline-editor';
import { isVideoSource, renderImage } from './utils/renderImage.ts';
import { canonicalVideoKey } from './utils/videoPreloader.ts';
import { getVideoPoster } from './utils/videoPosterCache.ts';
import { isVideoMediaUrl } from '../../services/assetUrlUtils.ts';
import {
  isBackgroundRecordingApp,
  isPixiAppDestroyed,
} from './utils/backgroundRecordingState.ts';

interface ImageRendererProps {
  app: Application | null;
  image: string | {
    src: string;
    startTime?: number;
    sourceType?: 'image' | 'video';
    mimeType?: string;
  } | null | undefined;
  width: number;
  height: number;
  backgroundImageRef: React.MutableRefObject<Sprite | null>;
  zIndex: number;
  timelineState?: React.MutableRefObject<TimelineState | null>;
  /**
   * Fired when the background is painted (or when there is nothing to load /
   * loading failed and we're done trying). Home uses the first call to lift
   * the boot overlay, so a first scene with video doesn't reveal a blank
   * canvas while the video still loads.
   */
  onBackgroundReady?: () => void;
}

type StagedSprite = Sprite & {
  __timelineVideoCleanup?: () => void;
};

/** Best-effort teardown of a background sprite and its media/listeners. */
const teardownSprite = (sprite: Sprite | null | undefined): void => {
  if (!sprite) return;
  try {
    sprite.parent?.removeChild(sprite);
    (sprite as StagedSprite).__timelineVideoCleanup?.();
    sprite.destroy();
  } catch {
    // Teardown races (e.g. app destroy mid-swap) must never break rendering.
  }
};

const ImageRenderer: React.FC<ImageRendererProps> = ({
  app,
  image,
  width,
  height,
  backgroundImageRef,
  zIndex,
  timelineState,
  onBackgroundReady
}) => {
  // Monotonic id so overlapping async loads can't mount out of order: only
  // the latest generation may touch the stage; stale ones destroy only what
  // they built.
  const generationRef = useRef(0);
  // Latest app seen, so the unmount teardown can respect background-recording
  // mode even after props stop updating.
  const appRef = useRef<Application | null>(null);
  appRef.current = app;
  // Ref-mirrored so the load effect never re-runs on callback identity.
  const onReadyRef = useRef(onBackgroundReady);
  onReadyRef.current = onBackgroundReady;

  const src = typeof image === 'string' ? image : image?.src;
  const startTime = (typeof image === 'object' ? image?.startTime : 0) ?? 0;
  const sourceType = image && src && isVideoSource(image, src) ? 'video' : 'image';
  const background = useMemo<{ src: string; startTime: number; sourceType: 'video' | 'image' } | null>(
    () => (src ? { src, startTime, sourceType } : null),
    [src, startTime, sourceType]
  );

  useEffect(() => {
    if (!app) return;
    const generation = ++generationRef.current;
    let cancelled = false;
    const controller = new AbortController();
    const pendingFrames: number[] = [];

    const fireReady = () => {
      if (!cancelled && generation === generationRef.current) {
        onReadyRef.current?.();
      }
    };

    // Sprite currently on stage. It stays mounted until the replacement's
    // first frame is drawn, so scene transitions hold the last frame instead
    // of flashing a white canvas while the next video loads. A failed load
    // likewise keeps the old frame up rather than clearing the canvas.
    const prevSprite = backgroundImageRef.current;

    // Nothing to load (e.g. no background yet): report ready immediately so
    // the boot overlay never waits on an image that will never come.
    if (!background) {
      fireReady();
      return () => {
        cancelled = true;
      };
    }

    const loadImage = async () => {
      let sprite: Sprite | undefined;
      let placeholder: Sprite | undefined;
      try {
        if (background && !isPixiAppDestroyed(app)) {
          // Instant placeholder: a previously captured first frame paints
          // immediately instead of a white canvas while the video streams.
          // Only when nothing is staged yet — scene transitions already hold
          // the last frame, so a poster there would flash. Never reports
          // ready: the video sprite still owns first-paint confirmation.
          if (!prevSprite && isVideoMediaUrl(background.src)) {
            const poster = getVideoPoster(canonicalVideoKey(background.src));
            if (poster) {
              placeholder = await renderImage(
                app, poster, width, height, timelineState, controller.signal
              );
              if (
                placeholder &&
                !cancelled &&
                generation === generationRef.current &&
                !isPixiAppDestroyed(app)
              ) {
                app.stage.addChild(placeholder);
                app.stage.setChildIndex(placeholder, zIndex);
                backgroundImageRef.current = placeholder;
              } else {
                teardownSprite(placeholder);
                placeholder = undefined;
              }
            }
          }
          sprite = await renderImage(
            app, background, width, height, timelineState, controller.signal
          );
        }
      } catch {
        sprite = undefined;
      }

      if (
        cancelled ||
        generation !== generationRef.current ||
        isPixiAppDestroyed(app)
      ) {
        // Superseded by a newer image (or torn down): never touch the stage,
        // just release what this run built. A staged placeholder is removed
        // only if no newer run adopted it as its previous frame.
        teardownSprite(sprite);
        if (placeholder && backgroundImageRef.current === placeholder) {
          backgroundImageRef.current = prevSprite;
          teardownSprite(placeholder);
        }
        return;
      }

      // Load failed (or nothing to stage): report done-trying so the boot
      // overlay falls back to the timeout instead of hanging, while the old
      // frame (if any) stays up.
      if (!sprite) {
        fireReady();
        return;
      }

      app.stage.addChild(sprite);
      app.stage.setChildIndex(sprite, zIndex);
      backgroundImageRef.current = sprite;

      // For video the resolved sprite already carries the decoded first
      // frame; wait two paint frames so "ready" means pixels on screen, not
      // just a staged texture. Images are decoded before staging: ready now.
      if (background && isVideoMediaUrl(background.src)) {
        const first = window.requestAnimationFrame(() => {
          const second = window.requestAnimationFrame(() => fireReady());
          pendingFrames.push(second);
        });
        pendingFrames.push(first);
      } else {
        fireReady();
      }

      if (prevSprite && prevSprite !== sprite) {
        teardownSprite(prevSprite);
      }
      if (placeholder && placeholder !== sprite) {
        teardownSprite(placeholder);
      }
    };

    loadImage();

    return () => {
      // Image change: do NOT tear down here — the previous sprite stays
      // staged under the succeeding run's ownership until its replacement is
      // ready (see above). Final teardown happens in the unmount effect.
      cancelled = true;
      controller.abort();
      pendingFrames.forEach((id) => window.cancelAnimationFrame(id));
    };
  }, [app, background, width, height, timelineState, zIndex, backgroundImageRef]);
  // Unmount only: release the staged sprite. Stage children die with the
  // Pixi app, but video elements and engine listeners need explicit cleanup.
  // Skipped for the background-recording app, whose stage must survive this
  // component unmounting mid-capture (mirrors the previous behavior).
  useEffect(() => {
    return () => {
      generationRef.current += 1;
      if (isBackgroundRecordingApp(appRef.current)) return;
      const sprite = backgroundImageRef.current;
      backgroundImageRef.current = null;
      teardownSprite(sprite);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

export default ImageRenderer;
